import { Carousel, Icon, ICONS, mixins, useMediaDevice } from '@bob/library'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import * as SC from '@websrc/App/Components/Lesson/Steps/Steps.style'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { mapAsset } from '@websrc/DataMappers/mapAsset'
import { mapStepsContent } from '@websrc/DataMappers/mapStepsContent'
import { styles } from 'leds'
import React, { FC } from 'react'
import { match } from 'ts-pattern'

export type StepContentProps = {
  content: ReturnType<typeof mapStepsContent>
}

const srcSetSettings = {
  sizes: '(min-width: 1024px) 600px',
  srcSetWidths: [320, 400, 600, 800, 1200],
}

const calculateImageSizes = (device: any) => {
  switch (device) {
    case 'desktop':
      return { height: 716, width: 1075 }
    case 'tablet':
      return { height: 337, width: 589 }
    default:
      return { height: 124, width: 216 }
  }
}

type StepContentAssetsProps = {
  assets: (ReturnType<typeof mapAsset> | undefined)[]
  type: 'images' | 'codeStacks'
}

const StepContentAssets: FC<StepContentAssetsProps> = ({ assets, type }) => {
  const device = useMediaDevice()

  const galleryPagination =
    device === 'desktop'
      ? assets.map((thumbnail, i) => (
          <SC.GalleryPaginationAsset
            as={Asset}
            height={55}
            key={i}
            sizes={'(min-width: 1024px) 85px'}
            srcSetWidths={[85]}
            thumbnail={thumbnail}
            video={undefined}
            width={85}
          />
        ))
      : undefined

  return (
    <>
      {assets.length &&
        assets.length <= 1 &&
        assets.map((thumbnail, i) => {
          if (thumbnail && type === 'images') {
            return (
              <SC.AssetWrapper key={i}>
                <Asset {...srcSetSettings} thumbnail={thumbnail} />
              </SC.AssetWrapper>
            )
          }

          if (thumbnail && type === 'codeStacks') {
            return (
              <SC.CodeStackWrapper key={i}>
                <Asset {...srcSetSettings} thumbnail={thumbnail} />
              </SC.CodeStackWrapper>
            )
          }

          return null
        })}

      {assets.length && assets.length > 1 && (
        // Display multiple images in carousel
        <>
          <SC.CarouselWrapper>
            <Carousel
              customActiveClass={styles({
                borderBottomWidth: 2,
                borderBottomStyle: 'solid',
                borderColor: 'blue5',
              })}
              customPagination={galleryPagination}
              displayBlockOnPrint={true}
              hidePaginationDots={true}
              itemsPerPage={1}
            >
              {assets.map((thumbnail, i) => (
                <SC.GalleryItem key={i}>
                  <SC.GalleryItemAsset
                    as={Asset}
                    height={calculateImageSizes(device).height}
                    lazyLoading={i !== 0}
                    sizes={'(min-width: 1024px) 800px, (min-width: 600px) 600px, 200px'}
                    srcSetWidths={[200, 600, 800]}
                    thumbnail={thumbnail}
                    video={undefined}
                    width={calculateImageSizes(device).width}
                  />
                </SC.GalleryItem>
              ))}
            </Carousel>
          </SC.CarouselWrapper>
          <SC.PrintWrapper>
            {assets.map((thumbnail, i) => (
              <SC.PrintItem key={i}>
                <label>{i + 1}</label>
                <SC.PrintItemAsset
                  as={Asset}
                  height={calculateImageSizes(device).height}
                  lazyLoading={i !== 0}
                  sizes={'(min-width: 1024px) 800px, (min-width: 600px) 600px, 200px'}
                  srcSetWidths={[200, 600, 800]}
                  thumbnail={thumbnail}
                  video={undefined}
                  width={calculateImageSizes(device).width}
                />
              </SC.PrintItem>
            ))}
          </SC.PrintWrapper>
        </>
      )}
    </>
  )
}

export const StepContent: FC<StepContentProps> = ({ content }) => {
  const t = useStaticText()

  return (
    <>
      {match(content)
        .with({ __dataType: 'text' }, (text) => <Markdown markdown={text.content} />)
        .with({ __dataType: 'asset' }, (asset) => (
          <SC.AssetWrapper>
            <Asset {...asset} {...srcSetSettings} />
          </SC.AssetWrapper>
        ))
        .with({ __dataType: 'images' }, ({ images }) => (
          <StepContentAssets assets={images} type={'images'} />
        ))
        .with({ __dataType: 'codeStacks' }, ({ codeStacks }) => (
          <StepContentAssets assets={codeStacks} type={'codeStacks'} />
        ))
        .with({ __dataType: 'warningText' }, ({ warningText }) => (
          <>
            <SC.WarningHeader>
              <Icon color={mixins.colors.corporateRed} icon={ICONS.WARNING} size={20} />

              <SC.WarningTitle>{t.plans_lessons_worksheets.warning_header}</SC.WarningTitle>
            </SC.WarningHeader>
            <SC.WarningMarkdown markdown={warningText} />
          </>
        ))
        .with({ __dataType: 'file' }, (fileData) => (
          <SC.FileContainer>
            <SC.ButtonPrimary href={fileData.file.url} target={'_blank'}>
              {fileData.buttonText}
            </SC.ButtonPrimary>
            {fileData.fileDescription && (
              <SC.FileDescription>{fileData.fileDescription}</SC.FileDescription>
            )}
          </SC.FileContainer>
        ))
        .with({ __dataType: 'overlayInstruction' }, ({ overlayInstruction }) => (
          <p>{overlayInstruction}</p>
        ))
        .otherwise(() => null)}
    </>
  )
}
